import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { clientService } from "../shared/client.service";
import { StatesServices } from "../../shared/services/states/states.service";
import { ToastrService } from "ngx-toastr";
import { MaskService } from "../../shared/services/mask/mask.service";
import { authenticationService } from "@app/login/auth.service";
import { Permissions } from "@models/permissions.enum";
import { ClientViewModel } from "../../shared/models/clients/ClientViewModel.model";
import { CanComponentDeactivate } from "../../shared/guards/confirmation/confirmation.guard";
import { ClientBilltoComponent } from "./tabs/client-billto/client-billto.component";

@Component({
  selector: "app-client-detail",
  templateUrl: "./client-detail.component.html",
  styleUrls: ["./client-detail.component.css"],
})
export class ClientDetailComponent implements OnInit, CanComponentDeactivate {
  @ViewChild("billToComponent") billToComponent: ClientBilltoComponent;

  client: ClientViewModel | any = {};
  isNewClient: boolean = false;
  isEditMode: boolean = false;
  isProcessing: boolean = false;
  selectedTab: string = "profile";
  hasUnsavedChanges: boolean = false;

  // Bill-to tracking
  newBillToList: any[] = [];
  editedBillToList: any[] = [];
  removedBillToList: any[] = [];

  // Data
  states: { abbreviation: string; fullName: string }[];
  originalInfo: any;

  // Masks
  dateInputMask: any;
  phoneMask: any;
  zipMask: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientService: clientService,
    private statesServices: StatesServices,
    private toastr: ToastrService,
    private maskService: MaskService,
    private authService: authenticationService
  ) {}

  ngOnInit() {
    this.initializeMasks();
    this.states = this.statesServices.getTwoLettersStates("US");

    this.route.params.subscribe((params) => {
      const clientId = params["clientId"];

      if (clientId === "newclient") {
        this.initializeNewClient();
      } else {
        this.loadExistingClient(clientId);
      }
    });
  }

  initializeMasks() {
    this.dateInputMask = this.maskService.getInputDateOnlyMask();
    this.phoneMask = this.maskService.getUsPhoneMask();
    this.zipMask = this.maskService.getUsZipMask();
  }

  initializeNewClient() {
    this.isNewClient = true;
    this.isEditMode = true;
    this.client = {
      isActive: true,
      validFrom: new Date(),
      hasPlantNumber: false,
      children: [],
    };
  }

  loadExistingClient(clientId: string) {
    this.isNewClient = false;
    this.isEditMode = false;

    this.clientService.getClientInfo(clientId).subscribe({
      next: (result) => {
        if (!result) {
          this.toastr.error("Client not found", "Error");
          return;
        }

        this.client = result;
        this.initDateFields(this.client);
        this.originalInfo = JSON.stringify(this.client);
      },
      error: (error) => {
        this.toastr.error("Error loading client: " + (error?.message || "Unknown error"), "Error");
      },
    });
  }

  hasPermission(permissionName: string): boolean {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  canViewProfileTab(): boolean {
    return (
      this.hasPermission("ClientsProfileRead") ||
      this.hasPermission("ClientsProfileChange") ||
      this.hasPermission("ClientsRead")
    );
  }

  canViewComplianceTab(): boolean {
    return (
      this.hasPermission("ClientsComplianceRead") || this.hasPermission("ClientsComplianceChange")
    );
  }

  canViewNotesTab(): boolean {
    return this.hasPermission("ClientsNotesRead") || this.hasPermission("ClientsNotesChange");
  }

  canViewDriverNotesTab(): boolean {
    return this.hasPermission("DriversNotesRead") || this.hasPermission("DriversNotesChange");
  }

  canViewBoardsTab(): boolean {
    return (
      this.hasPermission("ClientsBoardsRead") ||
      this.hasPermission("ClientsBoardsChange") ||
      this.canViewBillToTab()
    );
  }

  canViewBillToTab(): boolean {
    return this.hasPermission("ClientsBillToRead") || this.hasPermission("ClientsBillToChange");
  }

  canEditProfileTab(): boolean {
    return this.hasPermission("ClientsProfileChange");
  }

  canEditComplianceTab(): boolean {
    return this.hasPermission("ClientsComplianceChange");
  }

  canEditNotesTab(): boolean {
    return this.hasPermission("ClientsNotesChange");
  }

  canEditBoardsTab(): boolean {
    return this.hasPermission("ClientsBoardsChange") || this.canEditBillToTab();
  }

  canEditBillToTab(): boolean {
    return this.hasPermission("ClientsBillToChange");
  }

  // Bill To sub-tab permissions
  canViewBillToProfileTab(): boolean {
    return this.hasPermission("ClientsBillToProfileRead") || this.canViewBillToTab();
  }

  canViewBillToContactsTab(): boolean {
    return this.hasPermission("ClientsBillToContactsRead") || this.canViewBillToTab();
  }

  canViewBillToBillingTab(): boolean {
    return this.hasPermission("ClientsBillToBillingRead") || this.canViewBillToTab();
  }

  canEditBillToProfileTab(): boolean {
    return this.hasPermission("ClientsBillToProfileChange") || this.canEditBillToTab();
  }

  canEditBillToContactsTab(): boolean {
    return this.hasPermission("ClientsBillToContactsChange") || this.canEditBillToTab();
  }

  canEditBillToBillingTab(): boolean {
    return this.hasPermission("ClientsBillToBillingChange") || this.canEditBillToTab();
  }

  canEditAnyTab(): boolean {
    return (
      this.canEditProfileTab() ||
      this.canEditComplianceTab() ||
      this.canEditNotesTab() ||
      this.canEditBillToTab() ||
      this.hasPermission("ClientsChange")
    );
  }

  onTabClicked(tab: string) {
    this.selectedTab = tab;
  }

  onTabHasChanges(hasChanges: boolean) {
    this.hasUnsavedChanges = hasChanges;
  }

  onNewBillTosChange(newBillTos: any[]) {
    this.newBillToList = newBillTos;
  }

  onEditedBillTosChange(editedBillTos: any[]) {
    this.editedBillToList = editedBillTos;
  }

  onRemovedBillTosChange(removedBillTos: any[]) {
    this.removedBillToList = removedBillTos;
  }

  toggleEditMode() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.originalInfo = JSON.stringify(this.client);
    } else {
      this.saveClient();
    }
  }

  cancelEdit() {
    if (this.isNewClient) {
      this.router.navigate(["/clientlist"]);
    } else {
      this.isEditMode = false;
      this.hasUnsavedChanges = false;
      this.newBillToList = [];
      this.editedBillToList = [];
      this.removedBillToList = [];
      if (this.billToComponent) {
        this.billToComponent.clearAllTracking();
      }
      this.client = JSON.parse(this.originalInfo);
      this.initDateFields(this.client);
    }
  }

  saveClient() {
    if (!this.validateClient(this.client)) {
      return;
    }

    // Validate all bill-tos (existing children + new + edited)
    if (!this.validateAllBillTos()) {
      return;
    }

    this.isProcessing = true;

    if (this.isNewClient) {
      this.createNewClient();
    } else {
      this.updateExistingClient();
    }
  }

  validateAllBillTos(): boolean {
    // Validate existing bill-tos (children)
    if (this.client.children) {
      for (const billTo of this.client.children) {
        // Skip temp IDs (new bill-tos will be validated separately)
        if (billTo.id?.startsWith("temp-")) continue;
        if (!this.clientService.validateBillTo(billTo)) {
          return false;
        }
      }
    }

    // Validate new bill-tos
    for (const billTo of this.newBillToList) {
      if (!this.clientService.validateBillTo(billTo)) {
        return false;
      }
    }

    // Validate edited bill-tos
    for (const billTo of this.editedBillToList) {
      if (!this.clientService.validateBillTo(billTo)) {
        return false;
      }
    }

    // Validate date ranges
    if (!this.validateBillToDateRanges()) {
      return false;
    }

    // Check for duplicate bill-to IDs
    if (!this.checkBillToUnique()) {
      return false;
    }

    return true;
  }

  validateBillToDateRanges(): boolean {
    const allBillTos = [...(this.client.children || []), ...this.newBillToList].filter(
      (b) => !this.removedBillToList.some((r) => r.id === b.id)
    );

    for (const billTo of allBillTos) {
      if (billTo.validFrom && billTo.validTo && billTo.validFrom >= billTo.validTo) {
        this.toastr.warning(
          `${billTo.companyName || "Bill To"}: Valid From date must be prior to Valid To date.`,
          "Alert!"
        );
        return false;
      }
    }
    return true;
  }

  checkBillToUnique(): boolean {
    // Get all bill-tos to check (existing + new, excluding removed)
    const allBillTos = (this.client.children || []).filter(
      (b: any) => !this.removedBillToList.some((r) => r.id === b.id)
    );

    // Check each bill-to against all others
    for (let i = 0; i < allBillTos.length; i++) {
      const billTo = allBillTos[i];
      if (!billTo.customerNo) continue;

      const upperCustomerNo = billTo.customerNo.toUpperCase();

      // Check against all other bill-tos (not itself)
      for (let j = 0; j < allBillTos.length; j++) {
        if (i === j) continue; // Skip self

        const otherBillTo = allBillTos[j];
        if (!otherBillTo.customerNo) continue;

        if (upperCustomerNo === otherBillTo.customerNo.toUpperCase()) {
          this.toastr.warning(`Bill To ID "${billTo.customerNo}" already exists.`, "Alert!");
          return false;
        }
      }
    }
    return true;
  }

  createNewClient() {
    // For new clients, all bill-tos in children are new
    // Strip temp IDs before sending
    const billToList = (this.client.children || []).map((b: any) => {
      const copy = { ...b };
      if (copy.id?.startsWith("temp-")) {
        delete copy.id;
      }
      return copy;
    });

    this.clientService.createAddClient(this.client, billToList).subscribe(
      (clientCreated: any) => {
        this.finishClientCreation(clientCreated.id);
      },
      (errors) => {
        this.handleErrors(errors);
        this.isProcessing = false;
      }
    );
  }

  updateExistingClient() {
    this.clientService
      .updateClient(this.client, this.newBillToList, this.editedBillToList, this.removedBillToList)
      .subscribe({
        next: (response: any) => {
          const message = response?.message || "Client updated";
          this.toastr.success(message, "Success");
          this.isProcessing = false;
          this.isEditMode = false;
          this.hasUnsavedChanges = false;
          this.newBillToList = [];
          this.editedBillToList = [];
          this.removedBillToList = [];
          if (this.billToComponent) {
            this.billToComponent.clearAllTracking();
          }
          this.originalInfo = JSON.stringify(this.client);
          this.router.navigate(["/clientlist"]);
        },
        error: (errors) => {
          this.handleErrors(errors);
          this.isProcessing = false;
        },
      });
  }

  finishClientCreation(clientId: string) {
    this.toastr.success("Client created", "Success");
    this.isProcessing = false;
    this.isNewClient = false;
    this.isEditMode = false;
    this.hasUnsavedChanges = false;
    this.router.navigate([`/client-detail/${clientId}`]);
  }

  validateClient(client: any): boolean {
    if (!client.companyName) {
      this.toastr.warning("Company Name required", "Alert!");
      return false;
    }
    if (!client.customerNo) {
      this.toastr.warning("Client Id required", "Alert!");
      return false;
    }
    if (!client.validFrom) {
      this.toastr.warning("Valid From date required", "Alert!");
      return false;
    }
    if (!client.address) {
      this.toastr.warning("Address required", "Alert!");
      return false;
    }
    if (!client.city) {
      this.toastr.warning("City required", "Alert!");
      return false;
    }
    if (!client.state) {
      this.toastr.warning("State required", "Alert!");
      return false;
    }
    if (!client.zip) {
      this.toastr.warning("Zip required", "Alert!");
      return false;
    }
    return true;
  }

  handleErrors(errors: any) {
    if (Array.isArray(errors)) {
      for (const error of errors) {
        this.toastr.warning(error, "Alert!", {
          closeButton: true,
          enableHtml: true,
        });
      }
    } else {
      this.toastr.error(errors);
    }
  }

  initDateFields(client: any) {
    client.validFrom = this.convertToDate(client.validFrom);
    client.validTo = this.convertToDate(client.validTo);

    if (client.children) {
      client.children = client.children.map((child: any) => {
        child.validFrom = this.convertToDate(child.validFrom);
        child.validTo = this.convertToDate(child.validTo);
        return child;
      });
    }
  }

  convertToDate(date: any): Date | null {
    return date ? new Date(date) : null;
  }

  confirm(): boolean {
    if (this.hasUnsavedChanges || (this.isEditMode && !this.isNewClient)) {
      return window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    } else if (this.isNewClient) {
      return window.confirm("Leave this page without creating the client?");
    }
    return true;
  }

  getLocationString(): string {
    if (!this.client.city && !this.client.state) return "N/A";
    return `${this.client.city || ""}${this.client.city && this.client.state ? ", " : ""}${
      this.client.state || ""
    }`;
  }

  navigateToList() {
    this.router.navigate(["/clientlist"]);
  }
}
